import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { AliasBadge, StatusBadge } from '../components/Badges';
import { TrustLevelBadge } from '../components/TrustLevelBadge';
import {
  User,
  Building2,
  Mail,
  Phone,
  Shield,
  Activity,
  DollarSign,
  Receipt,
  MessageSquare,
  AlertTriangle,
  Ban,
  Unlock,
  RefreshCw,
  ArrowLeft,
  Calendar,
  Clock,
  CheckCircle2,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';

export default function AdminUserProfilePage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionDialog, setActionDialog] = useState(null); // {type: 'suspend'|'restrict'|'refund', data}
  const [actionReason, setActionReason] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, [userId]);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/users/${userId}/profile`);
      setProfile(res.data);
    } catch (err) {
      toast.error('Failed to load user profile');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async () => {
    if (!actionReason.trim()) {
      toast.error('Reason is required');
      return;
    }

    setProcessing(true);
    try {
      if (actionDialog.type === 'suspend') {
        await api.post(`/admin/ops/users/${userId}/pause`, { reason: actionReason });
        toast.success('User suspended successfully');
      } else if (actionDialog.type === 'resume') {
        await api.post(`/admin/ops/users/${userId}/resume`, { reason: actionReason });
        toast.success('User resumed successfully');
      } else if (actionDialog.type === 'refund') {
        await api.post(`/admin/transactions/${actionDialog.data.transaction_id}/refund`, {
          reason: actionReason,
        });
        toast.success('Refund processed successfully');
      }
      
      setActionDialog(null);
      setActionReason('');
      fetchProfile();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <div className="text-gray-500 font-mono text-sm">Loading user profile...</div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="max-w-7xl mx-auto px-6 py-12">
          <Button variant="ghost" onClick={() => navigate('/admin')} className="mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Admin Console
          </Button>
          <div className="text-center py-20 text-red-600">User not found</div>
        </div>
      </div>
    );
  }

  const user = profile.user;
  const verification = profile.verification;
  const stats = profile.stats;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-6 py-8" data-testid="admin-user-profile">
        {/* Back Button */}
        <Button variant="ghost" onClick={() => navigate('/admin')} className="mb-6" data-testid="back-to-admin">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Admin Console
        </Button>

        {/* User Header */}
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 bg-[#0055FF] text-white rounded-full flex items-center justify-center text-2xl font-semibold">
                {user.name?.charAt(0)?.toUpperCase() || 'U'}
              </div>
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="font-heading text-2xl font-semibold text-gray-900">{user.name}</h1>
                  <AliasBadge alias={user.alias} />
                  <Badge variant="outline" className={user.paused ? 'border-red-200 bg-red-50 text-red-700' : 'border-green-200 bg-green-50 text-green-700'}>
                    {user.paused ? 'Suspended' : 'Active'}
                  </Badge>
                </div>
                <div className="flex items-center gap-4 text-sm text-gray-600">
                  <div className="flex items-center gap-1.5">
                    <Mail className="w-4 h-4" />
                    {user.email}
                  </div>
                  {user.phone && (
                    <div className="flex items-center gap-1.5">
                      <Phone className="w-4 h-4" />
                      {user.phone}
                    </div>
                  )}
                  <div className="flex items-center gap-1.5">
                    <Building2 className="w-4 h-4" />
                    {user.company_name}
                  </div>
                </div>
                {user.abn && (
                  <div className="text-xs text-gray-500 font-mono mt-1">ABN: {user.abn}</div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2">
              {user.paused ? (
                <Button
                  size="sm"
                  variant="outline"
                  className="border-green-200 text-green-700 hover:bg-green-50"
                  onClick={() => setActionDialog({ type: 'resume' })}
                  data-testid="resume-user-btn"
                >
                  <Unlock className="w-4 h-4 mr-2" />
                  Resume Account
                </Button>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  className="border-red-200 text-red-700 hover:bg-red-50"
                  onClick={() => setActionDialog({ type: 'suspend' })}
                  data-testid="suspend-user-btn"
                >
                  <Ban className="w-4 h-4 mr-2" />
                  Suspend Account
                </Button>
              )}
            </div>
          </div>

          {/* Trust & Verification */}
          <div className="mt-6 pt-6 border-t border-gray-200 grid grid-cols-4 gap-6">
            <div>
              <div className="text-xs text-gray-500 mb-1.5">Trust Level</div>
              <TrustLevelBadge level={verification?.trust_level || 0} />
            </div>
            <div>
              <div className="text-xs text-gray-500 mb-1.5">Trust Score</div>
              <div className="font-semibold text-gray-900">{verification?.trust_score || 30}/100</div>
            </div>
            <div>
              <div className="text-xs text-gray-500 mb-1.5">Email Verified</div>
              <div className="flex items-center gap-1.5">
                {verification?.email_verified ? (
                  <><CheckCircle2 className="w-4 h-4 text-green-600" /><span className="text-sm text-green-700">Yes</span></>
                ) : (
                  <><XCircle className="w-4 h-4 text-gray-400" /><span className="text-sm text-gray-500">No</span></>
                )}
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-500 mb-1.5">Mobile Verified</div>
              <div className="flex items-center gap-1.5">
                {verification?.mobile_verified ? (
                  <><CheckCircle2 className="w-4 h-4 text-green-600" /><span className="text-sm text-green-700">Yes</span></>
                ) : (
                  <><XCircle className="w-4 h-4 text-gray-400" /><span className="text-sm text-gray-500">No</span></>
                )}
              </div>
            </div>
          </div>

          {user.paused && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <div className="text-xs font-semibold text-red-900 mb-1">Account Suspended</div>
              <div className="text-xs text-red-700">{user.pause_reason || 'No reason provided'}</div>
            </div>
          )}
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <StatCard
            icon={Receipt}
            label="Total Transactions"
            value={stats.total_transactions}
            subtext={`$${stats.total_spent?.toFixed(2) || '0.00'} spent`}
          />
          <StatCard
            icon={DollarSign}
            label="Total Revenue"
            value={`$${stats.total_revenue?.toFixed(2) || '0.00'}`}
            subtext={`${stats.leads_sold || 0} leads sold`}
          />
          <StatCard
            icon={AlertTriangle}
            label="Disputes"
            value={stats.disputes_count || 0}
            subtext={stats.disputes_resolved ? `${stats.disputes_resolved} resolved` : 'None'}
          />
          <StatCard
            icon={Calendar}
            label="Member Since"
            value={new Date(user.created_at).toLocaleDateString('en-AU', { month: 'short', year: 'numeric' })}
            subtext={`${Math.floor((Date.now() - new Date(user.created_at)) / (1000 * 60 * 60 * 24))} days ago`}
          />
        </div>

        {/* Tabs */}
        <Tabs defaultValue="activity" className="space-y-6">
          <TabsList>
            <TabsTrigger value="activity" data-testid="tab-activity">
              <Activity className="w-4 h-4 mr-2" />
              Lead Activity
            </TabsTrigger>
            <TabsTrigger value="financial" data-testid="tab-financial">
              <DollarSign className="w-4 h-4 mr-2" />
              Financial History
            </TabsTrigger>
            <TabsTrigger value="communications" data-testid="tab-communications">
              <MessageSquare className="w-4 h-4 mr-2" />
              Communications
            </TabsTrigger>
            <TabsTrigger value="audit" data-testid="tab-audit">
              <Shield className="w-4 h-4 mr-2" />
              Audit Log
            </TabsTrigger>
          </TabsList>

          {/* Lead Activity Tab */}
          <TabsContent value="activity">
            <Card>
              <CardHeader>
                <CardTitle>Lead Activity</CardTitle>
                <CardDescription>
                  {user.role === 'buyer' ? 'Leads purchased' : 'Leads listed and sold'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {profile.lead_activity && profile.lead_activity.length > 0 ? (
                  <div className="space-y-3">
                    {profile.lead_activity.map((activity, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                        <div className="flex-1">
                          <div className="font-mono text-sm text-gray-900">{activity.lead_id}</div>
                          <div className="text-xs text-gray-600 mt-1">
                            {activity.category} · {activity.status}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-semibold text-gray-900">${activity.amount?.toFixed(2) || '0.00'}</div>
                          <div className="text-xs text-gray-500">{new Date(activity.created_at).toLocaleDateString()}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500 text-sm">No lead activity</div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Financial History Tab */}
          <TabsContent value="financial">
            <Card>
              <CardHeader>
                <CardTitle>Financial History</CardTitle>
                <CardDescription>All transactions and payments</CardDescription>
              </CardHeader>
              <CardContent>
                {profile.transactions && profile.transactions.length > 0 ? (
                  <div className="space-y-3">
                    {profile.transactions.map((txn) => (
                      <div key={txn.transaction_id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-mono text-sm text-gray-900">{txn.transaction_id}</span>
                            <StatusBadge status={txn.status} />
                          </div>
                          <div className="text-xs text-gray-600">
                            {txn.lead_id} · {new Date(txn.created_at).toLocaleString()}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-semibold text-gray-900">${txn.amount.toFixed(2)}</div>
                          {txn.status === 'completed' && user.role === 'buyer' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-xs text-red-600 hover:text-red-700 h-6 mt-1"
                              onClick={() => setActionDialog({ type: 'refund', data: txn })}
                            >
                              <RefreshCw className="w-3 h-3 mr-1" />
                              Refund
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500 text-sm">No transactions</div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Communications Tab */}
          <TabsContent value="communications">
            <Card>
              <CardHeader>
                <CardTitle>Communications</CardTitle>
                <CardDescription>Deal Room messages and platform notifications</CardDescription>
              </CardHeader>
              <CardContent>
                {profile.communications && profile.communications.length > 0 ? (
                  <div className="space-y-3">
                    {profile.communications.map((comm, idx) => (
                      <div key={idx} className="p-3 border border-gray-200 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <div className="text-xs font-semibold text-gray-900">{comm.type}</div>
                          <div className="text-xs text-gray-500">{new Date(comm.created_at).toLocaleString()}</div>
                        </div>
                        <div className="text-sm text-gray-700">{comm.content}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500 text-sm">No communications</div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Audit Log Tab */}
          <TabsContent value="audit">
            <Card>
              <CardHeader>
                <CardTitle>Audit Log</CardTitle>
                <CardDescription>All actions and events for this user</CardDescription>
              </CardHeader>
              <CardContent>
                {profile.audit_log && profile.audit_log.length > 0 ? (
                  <div className="space-y-2">
                    {profile.audit_log.map((log, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-2 hover:bg-gray-50 rounded">
                        <Clock className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-gray-900">{log.action}</div>
                          <div className="text-xs text-gray-600 font-mono mt-0.5">
                            {log.target_type}: {log.target_id}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">{new Date(log.timestamp).toLocaleString()}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500 text-sm">No audit logs</div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Action Dialog */}
      <Dialog open={!!actionDialog} onOpenChange={() => setActionDialog(null)}>
        <DialogContent data-testid="action-dialog">
          <DialogHeader>
            <DialogTitle>
              {actionDialog?.type === 'suspend' && 'Suspend User Account'}
              {actionDialog?.type === 'resume' && 'Resume User Account'}
              {actionDialog?.type === 'refund' && 'Process Refund'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {actionDialog?.type === 'refund' && (
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-md">
                <div className="text-xs text-gray-600 mb-1">Transaction</div>
                <div className="font-mono text-sm">{actionDialog.data?.transaction_id}</div>
                <div className="text-lg font-semibold text-gray-900 mt-2">
                  ${actionDialog.data?.amount.toFixed(2)}
                </div>
              </div>
            )}
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason <span className="text-red-600">*</span>
              </label>
              <Textarea
                value={actionReason}
                onChange={(e) => setActionReason(e.target.value)}
                placeholder="Provide a detailed reason for this action..."
                rows={4}
                data-testid="action-reason"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialog(null)} disabled={processing}>
              Cancel
            </Button>
            <Button
              onClick={handleAction}
              disabled={processing || !actionReason.trim()}
              className={actionDialog?.type === 'suspend' ? 'bg-red-600 hover:bg-red-700' : ''}
              data-testid="confirm-action-btn"
            >
              {processing ? 'Processing...' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Stat Card Component
function StatCard({ icon: Icon, label, value, subtext }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
            <Icon className="w-5 h-5 text-[#0055FF]" />
          </div>
          <div className="flex-1">
            <div className="text-xs text-gray-600">{label}</div>
            <div className="text-xl font-semibold text-gray-900">{value}</div>
          </div>
        </div>
        {subtext && <div className="text-xs text-gray-500">{subtext}</div>}
      </CardContent>
    </Card>
  );
}
